import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, DollarSign, Kanban, Trophy, Target, Clock, AlertCircle, Flame, Users, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAppearance } from "../contexts/AppearanceContext";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

function fmt(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(v || 0);
}
function fmtK(v) {
  if (v >= 1000000) return `R$ ${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(0)}k`;
  return fmt(v);
}

function KpiCard({ title, value, sub, subColor, icon: Icon, tc500 }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ backgroundColor: `${tc500}18` }}>
          <Icon className="w-4 h-4" style={{ color: tc500 }} />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900 leading-none mb-1.5">{value}</p>
      {sub && <p className={`text-xs font-medium ${subColor || "text-slate-400"}`}>{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [pipelineId, setPipelineId] = useState("");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (pipelineId) params.set("pipeline_id", pipelineId);
      const res = await axios.get(`${API}/dashboard?${params}`, { withCredentials: true });
      setData(res.data);
      // Set default pipeline on first load
      if (!pipelineId && res.data.pipelines?.length > 0) {
        setPipelineId(res.data.pipelines[0].id);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [period, pipelineId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: tc500, borderTopColor: "transparent" }} />
          <p className="text-sm text-slate-400">{t("ui.carregando_dashboard")}</p>
        </div>
      </div>
    );
  }

  const { kpis, funil, monthly, por_responsavel, motivos_perda, alertas, deals_quentes, pipelines } = data;
  const PERIOD_LABELS = { month: t("dashboard.periodMonth"), quarter: t("dashboard.periodQuarter"), year: t("dashboard.periodYear") };

  return (
    <div className="space-y-6">
      {/* Header + Filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{t("dashboard.title")}</h1>
          <p className="text-slate-500 mt-0.5">{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={pipelineId} onValueChange={setPipelineId}>
            <SelectTrigger className="w-40 h-9 text-sm"><SelectValue placeholder="Pipeline" /></SelectTrigger>
            <SelectContent>
              {(pipelines || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">{t("dashboard.currentMonth")}</SelectItem>
              <SelectItem value="quarter">{t("dashboard.currentQuarter")}</SelectItem>
              <SelectItem value="year">{t("dashboard.currentYear")}</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={fetchDashboard} className="h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard title={t("dashboard.mrrOpen")} value={fmtK(kpis.mrr_aberto)} sub={`${kpis.negocios_abertos} ${t("dashboard.deals")}`} icon={DollarSign} tc500={tc500} />
        <KpiCard title={t("dashboard.tcvOpen")} value={fmtK(kpis.tcv_aberto)} sub="MRR×12 + Setup" icon={TrendingUp} tc500={tc500} />
        <KpiCard title={`${t("dashboard.wonMrr")} (${PERIOD_LABELS[period]})`} value={fmtK(kpis.mrr_ganho_period)}
          sub={kpis.mrr_ganho_diff >= 0 ? `↑ ${kpis.mrr_ganho_diff}% ${t("dashboard.vsAnterior")}` : `↓ ${Math.abs(kpis.mrr_ganho_diff)}% ${t("dashboard.vsAnterior")}`}
          subColor={kpis.mrr_ganho_diff >= 0 ? "text-emerald-500" : "text-rose-500"}
          icon={Trophy} tc500={tc500} />
        <KpiCard title={t("dashboard.openDeals")} value={kpis.negocios_abertos}
          sub={`${kpis.ganhos_period} ${t("dashboard.wonVsLostChart")} · ${kpis.perdidos_period} ${t("dashboard.lostChart")}`}
          icon={Kanban} tc500={tc500} />
        <KpiCard title={t("dashboard.wonRate")} value={`${kpis.taxa_conversao}%`}
          sub={`${kpis.ganhos_period} ${t("dashboard.wonThisPeriod")} ${PERIOD_LABELS[period]}`}
          subColor={kpis.taxa_conversao >= 50 ? "text-emerald-500" : kpis.taxa_conversao >= 30 ? "text-amber-500" : "text-rose-500"}
          icon={Target} tc500={tc500} />
        <KpiCard title={t("dashboard.avgTime")} value={`${kpis.avg_close_days}d`}
          sub={t("dashboard.creationToWon")} icon={Clock} tc500={tc500} />
      </div>

      {/* Funil + Motivos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Kanban className="w-4 h-4" style={{ color: tc500 }} />{t("dashboard.funnel")}
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={funil} layout="vertical" margin={{ top: 0, right: 40, left: 80, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={76} />
              <RechartTooltip formatter={(v) => [`${v} deals`, "Negócios"]} contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {funil.map((e, i) => <Cell key={i} fill={e.color || tc500} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {funil.map((s, i) => (
              <div key={i} className="flex-shrink-0 text-center px-3 py-2 rounded-lg bg-slate-50 min-w-[80px] border border-slate-100">
                <p className="text-lg font-bold text-slate-800">{s.count}</p>
                <p className="text-[10px] text-slate-400 truncate max-w-[76px]">{s.name}</p>
                <p className="text-[10px] font-semibold" style={{ color: tc500 }}>{fmtK(s.mrr)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-500" />{t("dashboard.lossReasons")}
          </h3>
          {motivos_perda.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-300 text-sm">Sem perdas registradas</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie data={motivos_perda} cx="50%" cy="50%" outerRadius={65} dataKey="value">
                    {motivos_perda.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <RechartTooltip contentStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {motivos_perda.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-slate-600 truncate max-w-[110px]">{m.name}</span>
                    </div>
                    <span className="font-semibold text-slate-700">{m.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Gráficos temporais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: tc500 }} />{t("dashboard.mrrByMonth")}
          </h3>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={monthly} margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
              <RechartTooltip formatter={v => [fmtK(v), "MRR Ganho"]} contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="mrr_ganho" fill={tc500} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Target className="w-4 h-4" style={{ color: tc500 }} />{t("dashboard.wonVsLost")}
          </h3>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={monthly} margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartTooltip contentStyle={{ fontSize: 12 }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="ganhos" name={t("dashboard.wonVsLostChart")} fill="#10b981" radius={[4, 4, 0, 0]} stackId="a" />
              <Bar dataKey="perdidos" name={t("dashboard.lostChart")} fill="#ef4444" radius={[4, 4, 0, 0]} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Responsáveis + Alertas + Deals Quentes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" style={{ color: tc500 }} />{t("dashboard.performance")}
          </h3>
          <div className="space-y-3">
            {por_responsavel.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">{t("dashboard.noData")}</p> :
              [...por_responsavel].sort((a, b) => b.mrr_aberto - a.mrr_aberto).slice(0, 6).map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                    {(r.responsavel || t("dashboard.noOwner")).charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-semibold text-slate-700 truncate">{r.responsavel || t("dashboard.noOwner")}</span>
                      <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ color: tc500 }}>{fmtK(r.mrr_aberto)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                      <span>{r.abertos} {t("dashboard.open")}</span>
                      <span className="text-emerald-500">{r.ganhos}{t("dashboard.won")}</span>
                      <span className="text-rose-400">{r.perdidos}{t("dashboard.lost")}</span>
                      <span className="ml-auto font-semibold text-slate-600">{r.taxa}%</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />{t("dashboard.slaAlerts")}
            {alertas.length > 0 && <Badge className="ml-auto bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5">{alertas.length}</Badge>}
          </h3>
          {alertas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-300">
              <AlertCircle className="w-8 h-8 mb-2" />
              <p className="text-sm">Nenhum alerta ativo</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alertas.map((a, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{a.titulo}</p>
                    <p className="text-[10px] text-slate-500">{a.stage} · {a.days}d · {a.responsavel}</p>
                    <p className="text-[10px] text-amber-600 font-medium">{a.tipo === "prazo" ? t("dashboard.exceededDeadline") : t("sla.inactivity")}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Flame className="w-4 h-4 text-rose-500" />{t("dashboard.hotDeals")}
            <span className="text-[10px] text-slate-400">{t("dashboard.lastStages")}</span>
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {deals_quentes.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">{t("dashboard.noData")}</p> :
              deals_quentes.map((d, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: tc500 }}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{d.titulo}</p>
                    <p className="text-[10px] text-slate-400">{d.stage} · {d.dias_na_etapa}d</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-emerald-600">{fmtK(d.mrr)}/{t("dashboard.periodMonth")}</p>
                    <p className="text-[10px] text-slate-400">TCV {fmtK(d.tcv)}</p>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}
